import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { getGroups, switchGroup } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search, Sun, Moon, Globe, Menu, X, Package, PlusCircle, Boxes, Building2, ChevronsUpDown, Check, Command,
} from 'lucide-react';
import { toast } from 'sonner';
import { NotificationBell } from './NotificationBell';
import { MainNav } from './MainNav';
import { MobileBottomNav } from './MobileBottomNav';
import { MobileMenu } from './MobileMenu';
import { UserMenu } from './UserMenu';
import { CommandPalette } from './CommandPalette';
import { Breadcrumbs } from './Breadcrumbs';
import { useNavBadges } from '@/hooks/useNavBadges';

const languages = [
  { code: 'en', name: 'English', flag: 'EN' },
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'es', name: 'Español', flag: 'ES' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'it', name: 'Italiano', flag: 'IT' },
  { code: 'pt', name: 'Português', flag: 'PT' },
  { code: 'ro', name: 'Română', flag: 'RO' },
];

export const Header = ({ onSearch }) => {
  const { t, i18n } = useTranslation();
  const { user, logout, updateLanguage, refreshUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [switchingGroup, setSwitchingGroup] = useState(false);
  const badges = useNavBadges(user);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) getGroups().then((res) => setGroups(res.data)).catch(() => {});
  }, [isAdmin]);

  const handleGroupSwitch = async (groupId) => {
    if (groupId === user?.group_id) return;
    setSwitchingGroup(true);
    try {
      const res = await switchGroup(groupId);
      await refreshUser();
      toast.success(t('nav.groupSwitched', { name: res.data.group_name }));
      window.location.reload();
    } catch {
      toast.error(t('nav.groupSwitchFailed'));
    } finally {
      setSwitchingGroup(false);
    }
  };

  const currentGroupName = groups.find((g) => g.group_id === user?.group_id)?.group_name;

  const handleAction = (action) => {
    if (action === 'restartTour') {
      localStorage.removeItem('surplify_onboarding_done');
      navigate('/');
      setTimeout(() => window.dispatchEvent(new Event('surplify:start-tour')), 150);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      if (onSearch) onSearch(searchQuery);
    }
  };

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    updateLanguage(code);
  };

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
            <Package className="h-8 w-8 text-orange-500" />
            <span className="hidden sm:block font-manrope font-bold text-lg tracking-tight">Surplify</span>
          </Link>

          <form onSubmit={handleSearch} className="flex-1 max-w-2xl hidden md:flex">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-20 h-10 w-full bg-slate-100 dark:bg-slate-800 border-0 focus-visible:ring-orange-500"
                data-testid="search-input"
              />
              {user && (
                <button
                  type="button"
                  onClick={() => setCmdOpen(true)}
                  title={t('cmd.hint')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 hidden lg:flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground hover:text-foreground hover:border-orange-400 transition-colors"
                  data-testid="cmd-open-button"
                >
                  <Command className="h-3 w-3" /> K
                </button>
              )}
            </div>
            <Button type="submit" className="ml-2 bg-orange-500 hover:bg-orange-600" data-testid="search-button">
              <Search className="h-4 w-4" />
            </Button>
          </form>

          <div className="flex items-center gap-2">
            {isAdmin && groups.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={switchingGroup} data-testid="admin-group-switcher"
                    className="hidden sm:flex gap-1.5 border-amber-500 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20 max-w-[180px]">
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    <span className="text-xs truncate">{currentGroupName || 'Gruppe'}</span>
                    <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{t('nav.switchGroup')}</div>
                  <DropdownMenuSeparator />
                  {groups.map((g) => (
                    <DropdownMenuItem key={g.group_id} onClick={() => handleGroupSwitch(g.group_id)} className="flex items-center justify-between" data-testid={`switch-group-${g.group_id}`}>
                      <span className="truncate">{g.group_name}</span>
                      {g.group_id === user?.group_id && <Check className="h-4 w-4 text-amber-500 shrink-0 ml-2" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {user && (
              <>
                <Button size="sm" variant="outline" onClick={() => navigate('/my-materials')} data-testid="my-materials-button"
                  className="hidden lg:flex gap-1 border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20">
                  <Boxes className="h-4 w-4" />
                  <span className="text-xs">{t('nav.myMaterials')}</span>
                </Button>
                <Button size="sm" onClick={() => navigate('/material/new')} data-testid="create-material-button"
                  className="hidden sm:flex bg-orange-500 hover:bg-orange-600 gap-1">
                  <PlusCircle className="h-4 w-4" />
                  <span className="text-xs">{t('create.newTitle')}</span>
                </Button>
              </>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="hidden sm:flex gap-1" data-testid="language-selector">
                  <Globe className="h-4 w-4" />
                  <span className="text-xs font-medium">{currentLang.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem key={lang.code} onClick={() => handleLanguageChange(lang.code)}
                    className={i18n.language === lang.code ? 'bg-orange-50 dark:bg-orange-900/20' : ''} data-testid={`lang-${lang.code}`}>
                    <span className="font-medium mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {user && <NotificationBell />}
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {user ? (
              <UserMenu user={user} badges={badges} onAction={handleAction} onLogout={logout} />
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" asChild data-testid="login-button">
                  <Link to="/login">{t('nav.login')}</Link>
                </Button>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600" asChild data-testid="register-button">
                  <Link to="/register">{t('nav.register')}</Link>
                </Button>
              </div>
            )}

            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        <div className="md:hidden pb-3">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder={t('common.search')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 h-10 w-full bg-slate-100 dark:bg-slate-800 border-0" data-testid="mobile-search-input" />
            </div>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600"><Search className="h-4 w-4" /></Button>
          </form>
        </div>
      </div>

      {user && <MainNav badges={badges} />}
      {user && <Breadcrumbs />}

      {mobileMenuOpen && (
        <MobileMenu
          user={user} badges={badges} languages={languages} currentLang={i18n.language}
          onLanguageChange={handleLanguageChange} onAction={handleAction} onLogout={logout}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}

      {user && <MobileBottomNav badges={badges} />}
      {user && <CommandPalette user={user} open={cmdOpen} onOpenChange={setCmdOpen} onAction={handleAction} />}
    </header>
  );
};
